import { useNavigate, useLocation } from 'react-router-dom';
import { useUserRole } from '@/hooks/useUserRole';
import { useUnreadAudioCount } from '@/hooks/useUnreadAudioCount';
import { useUnreadWritingCount } from '@/hooks/useUnreadWritingCount';
import { useClientThreads } from '@/hooks/useClientThreads';
import { useCoachThreads } from '@/hooks/useCoachThreads';
import { Badge } from '@/components/ui/badge';
import type { Database } from '@/integrations/supabase/types';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
  Home, 
  Calendar, 
  CheckSquare, 
  Mic, 
  BookOpen,
  Target,
  Users,
  User,
  TrendingUp,
  Megaphone,
  Sparkles,
  MessageSquareMore,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface NavItem {
  path: string;
  icon: LucideIcon;
  label: string;
  active: boolean;
  badge?: number;
  hasNotification?: boolean;
  onClick?: () => void;
}

interface MobileBottomNavProps {
  packageType: Database['public']['Enums']['package_type'] | null;
  avatarUrl?: string | null;
  initials?: string;
}

export function MobileBottomNav({ packageType, avatarUrl, initials }: MobileBottomNavProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isCoach, isConversationPartner } = useUserRole();
  const unreadAudioCount = useUnreadAudioCount();
  const { unreadCount: unreadWritingCount } = useUnreadWritingCount();
  const { unreadCount: clientUnreadCount } = useClientThreads();
  const { hasUnread: coachHasUnread } = useCoachThreads();

  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/') || location.pathname.startsWith(path + '?');

  // Check if current path matches posts-related routes
  const isPostsActive = ['/announcements'].some(
    p => location.pathname === p || location.pathname.startsWith(p + '/')
  );

  // Check if current path matches growth-related routes  
  const isGrowthActive = ['/growth', '/goals'].some(
    p => location.pathname === p || location.pathname.startsWith(p + '/')
  );

  // Client nav: Home | Tasks | Vocab | Posts | Messages
  const clientNavItems: NavItem[] = [
    { path: '/', icon: Home, label: 'Home', active: location.pathname === '/' },
    { path: '/tasks', icon: CheckSquare, label: 'To-Dos', active: isActive('/tasks') },
    { path: '/vocabulary', icon: BookOpen, label: 'Vocab', active: isActive('/vocabulary') },
    { path: '/announcements', icon: Megaphone, label: 'Posts', active: isPostsActive },
    {
      path: '#chat',
      icon: MessageSquareMore,
      label: 'Messages',
      active: false,
      badge: clientUnreadCount,
      onClick: () => window.dispatchEvent(new CustomEvent('open-chat-panel')),
    },
  ];

  const isCoachPracticeActive = ['/practice', '/vocabulary', '/audio', '/writing'].some(
    p => location.pathname === p || location.pathname.startsWith(p + '/')
  );

  const coachNavItems: NavItem[] = [
    { path: '/', icon: Home, label: 'Home', active: location.pathname === '/' },
    { path: '/admin', icon: Users, label: 'Admin', active: isActive('/admin') },
    { path: '/practice', icon: Target, label: 'Practice', active: isCoachPracticeActive, badge: unreadAudioCount + unreadWritingCount },
    { path: '/coaching-assistant', icon: Sparkles, label: 'Assistant', active: isActive('/coaching-assistant') },
    {
      path: '#chat',
      icon: MessageSquareMore,
      label: 'Messages',
      active: false,
      hasNotification: coachHasUnread,
      onClick: () => window.dispatchEvent(new CustomEvent('open-coach-chat-panel')),
    },
  ];

  const conversationPartnerNavItems: NavItem[] = [
    { path: '/', icon: Home, label: 'Home', active: location.pathname === '/' },
    { path: '/sessions', icon: Calendar, label: 'Sessions', active: isActive('/sessions') },
    { path: '/profile', icon: User, label: 'Profile', active: isActive('/profile') },
  ];

  const navItems: NavItem[] = isConversationPartner
    ? conversationPartnerNavItems 
    : isCoach 
      ? coachNavItems 
      : clientNavItems;

  return (
    <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-card border-t border-border shadow-lg safe-area-bottom">
      <div className="flex items-center justify-around h-16 px-2">
        {navItems.map((item) => (
          <button
            key={item.path}
            onClick={() => {
              if (item.onClick) {
                item.onClick();
              } else {
                navigate(item.path);
              }
            }}
            className={cn(
              "flex flex-col items-center justify-center flex-1 h-full min-w-0 px-1 py-2 transition-[color,opacity] duration-150 relative select-none",
              "active:scale-[0.98] active:opacity-80",
              "[&>div>svg]:transition-transform [&>div>svg]:duration-200 [&>div>svg]:ease-out active:[&>div>svg]:scale-110",
              item.active 
                ? "text-primary" 
                : "text-muted-foreground hover:text-foreground"
            )}
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <div className="relative">
              {item.label === 'Profile' && avatarUrl ? (
                <Avatar className="h-5 w-5">
                  <AvatarImage src={avatarUrl} alt="Profile" />
                  <AvatarFallback className="bg-primary text-primary-foreground text-[8px]">
                    {initials}
                  </AvatarFallback>
                </Avatar>
              ) : (
                <item.icon className="h-5 w-5" />
              )}
              {'badge' in item && typeof item.badge === 'number' && item.badge > 0 && (
                <Badge 
                  variant="destructive" 
                  className="absolute -top-2 -right-2 h-4 min-w-[16px] text-[10px] flex items-center justify-center p-0 px-1"
                >
                  {item.badge}
                </Badge>
              )}
              {item.hasNotification && (
                <span className="absolute -top-1.5 -right-1.5 w-3 h-3 rounded-full bg-destructive border-2 border-card shadow" />
              )}
            </div>
            <span className={cn(
              "text-[10px] mt-1 truncate max-w-full",
              item.active ? "font-medium" : ""
            )}>
              {item.label}
            </span>
          </button>
        ))}
      </div>
    </nav>
  );
}
